// v4 — Mark's Insights: a full dashboard for Mark, the AI media buyer.
import React from 'react';
import { v4data, TIER, GOALS } from './v4data';
import { fmt$, fmtK, fmtX, pct } from './v4ui';
import markPhoto from 'static/images/v4/mark.png';
import './dashboard.scss';

const short = (n) => n.split('—').pop().trim();

// vibrant app palette (light stop -> saturated stop) for the layered icons
const PAL = {
  mint: { l: '#8df5c4', b: '#12e08a' },
  teal: { l: '#7ff0dd', b: '#0bcbb0' },
  cyan: { l: '#7fd6ff', b: '#12aaff' },
  blue: { l: '#a2b6ff', b: '#3b6bff' },
  indigo: { l: '#968fff', b: '#5A54F3' },
  violet: { l: '#b3a4ff', b: '#7a3cff' },
  amber: { l: '#ffe08a', b: '#f7b40f' },
  pink: { l: '#ffb0da', b: '#f45fb0' },
  magenta: { l: '#ff96c9', b: '#e6318f' },
  rose: { l: '#ffabb4', b: '#fb4d63' },
};

// simple iconic shapes in vibrant palette gradients, layered with transparency
function LayeredIco({ shape, colors }) {
  const uid = shape + '-' + colors.join('');
  const op = [0.72, 0.82, 0.92];
  const defs = colors.map((name, i) => {
    const c = PAL[name];
    return (
      <linearGradient id={`${uid}-${i}`} key={i} x1="0.1" y1="0" x2="0.9" y2="1">
        <stop offset="0" stopColor={c.l} />
        <stop offset="1" stopColor={c.b} />
      </linearGradient>
    );
  });
  const fill = (i) => ({ fill: `url(#${uid}-${i})`, opacity: op[i] });
  let shapes;
  if (shape === 'circles') {
    const pos = [[16, 26], [24, 21.5], [32, 26]];
    shapes = pos.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="12.5" {...fill(i)} />);
  } else if (shape === 'squares') {
    const pos = [[6, 6.5], [13, 13], [20, 19.5]];
    shapes = pos.map((p, i) => <rect key={i} x={p[0]} y={p[1]} width="20.5" height="20.5" rx="6" {...fill(i)} />);
  } else if (shape === 'dollars') {
    // 3 upright dollar signs overlapping, climbing up to the right
    const P = [[13, 38], [22, 29], [31, 20]];
    shapes = P.map((p, i) => (
      <text key={i} x={p[0]} y={p[1]} textAnchor="middle" fontSize="24"
        fontWeight="800" fontFamily="'Inter', system-ui, sans-serif"
        stroke={`url(#${uid}-${i})`} strokeWidth="1.3" strokeLinejoin="round"
        paintOrder="stroke" {...fill(i)}>$</text>
    ));
  } else if (shape === 'triUpRight') {
    // mirror of triDown: 3 equal triangles pointing straight up, staggered up to the right
    const s = 20, h = 17.3;
    const up = (x, y) => `M ${x} ${y} L ${x + s} ${y} L ${x + s / 2} ${y - h} Z`;
    const P = [[8, 40], [14, 34], [20, 28]];
    shapes = P.map((p, i) => <path key={i} d={up(p[0], p[1])} {...fill(i)} />);
  } else if (shape === 'triUp') {
    const tri = (cx, hw, apex) => `M ${cx} ${apex} L ${cx + hw} 39 L ${cx - hw} 39 Z`;
    const P = [[16, 12.5, 13], [24, 13, 8], [32, 12.5, 13]];
    shapes = P.map((p, i) => <path key={i} d={tri(p[0], p[1], p[2])} {...fill(i)} />);
  } else if (shape === 'triDown') {
    // 3 equal triangles all pointing straight down, staggered diagonally
    const s = 20, h = 17.3;
    const down = (x, y) => `M ${x} ${y} L ${x + s} ${y} L ${x + s / 2} ${y + h} Z`;
    const P = [[8, 8], [14, 14], [20, 20]];
    shapes = P.map((p, i) => <path key={i} d={down(p[0], p[1])} {...fill(i)} />);
  } else { // peaks (the adaya-mark motif)
    const peak = (cx, half, top) => `M ${cx - half} 40 Q ${cx - half} ${top + 5} ${cx} ${top} Q ${cx + half} ${top + 5} ${cx + half} 40 Z`;
    const P = [[16, 10, 22], [24, 12, 14], [31, 10, 8]];
    shapes = P.map((p, i) => <path key={i} d={peak(p[0], p[1], p[2])} {...fill(i)} />);
  }
  return (
    <svg viewBox="0 0 48 48" fill="none">
      <defs>{defs}</defs>
      {shapes}
    </svg>
  );
}

const SparkTag = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 3l1.5 6 6 1.5-6 1.5L12 18l-1.5-6L4.5 10.5 10.5 9z" /></svg>
);

export default function InsightsV4() {
  const D = v4data(30);
  const camps = D.ds.campaigns.map((c) => ({ c, m: D.campSum(c) })).sort((a, b) => b.m.roas - a.m.roas);
  const best = camps[0], worst = camps[camps.length - 1];
  const roasChg = pct(D.cur.roas, D.prev.roas);
  const proj = D.cur.revenue * 1.03;
  const realloc = D.cur.revenue * 0.04;
  const next7 = D.cur.roas * 1.03;
  const paceOver = (proj / GOALS.revenue - 1) * 100;
  const worstGap = (worst.m.roas / GOALS.mer - 1) * 100;
  const next7Chg = (next7 / D.cur.roas - 1) * 100;

  const cards = [
    { shape: 'triDown', colors: ['amber', 'rose', 'magenta'], title: roasChg >= 0 ? 'Efficiency is climbing' : 'Efficiency dipped', tag: 'From the last 30 days',
      dir: roasChg >= 0 ? 'up' : 'down', delta: `${Math.abs(roasChg).toFixed(1)}%`,
      body: <>Blended ROAS is <b>{roasChg >= 0 ? 'up' : 'down'} {Math.abs(roasChg).toFixed(1)}%</b> on the prior 30 days. Retargeting is doing the heavy lifting and I'm feeding it more.</> },
    { shape: 'triUpRight', colors: ['mint', 'cyan', 'blue'], title: "You're ahead of pace", tag: 'From month-to-date pace',
      dir: paceOver >= 0 ? 'up' : 'down', delta: `${Math.abs(paceOver).toFixed(1)}%`,
      body: <>You're tracking to <b>{fmtK(proj)}</b> against your <b>{fmtK(GOALS.revenue)}</b> goal. I've got you landing over plan if I hold this spend mix.</> },
    { shape: 'squares', colors: ['teal', 'indigo', 'violet'], title: 'Your top mover', tag: 'From live spend',
      dir: 'up', delta: `${fmtX(best.m.roas)}`,
      body: <><b>{short(best.c.name)}</b> is my most efficient line at <b>{fmtX(best.m.roas)}</b>. I'm scaling it carefully so I don't spike your CPMs.</> },
    { shape: 'triUp', colors: ['amber', 'pink', 'rose'], title: 'One to watch', tag: 'From live spend',
      dir: 'down', delta: `${Math.abs(worstGap).toFixed(0)}%`,
      body: <><b>{short(worst.c.name)}</b> is at <b>{fmtX(worst.m.roas)}</b>, under target. I'm trimming its budget and testing fresh creative before it drags the blend.</> },
    { shape: 'squares', colors: ['teal', 'cyan', 'indigo'], title: "Budget I'd move", tag: 'From your efficiency curve',
      dir: 'up', delta: `${fmtK(realloc)}/mo`,
      body: <>Shifting <b>~15%</b> off weak prospecting into retargeting could add <b>~{fmt$(realloc)}</b> a month at today's efficiency. Say the word.</> },
    { shape: 'dollars', colors: ['mint', 'cyan', 'violet'], title: "Every dollar's working", tag: 'From blended performance',
      // money = green -> blue -> purple
      body: <>Blended MER is <b>{fmtX(D.cur.roas)}</b>. Every <b>$1</b> I spend returns <b>${D.cur.roas.toFixed(2)}</b> in revenue, above your {GOALS.mer}x goal.</> },
    { shape: 'squares', colors: ['cyan', 'blue', 'indigo'], title: 'Next 7 days', tag: 'From my 7-day model',
      dir: next7Chg >= 0 ? 'up' : 'down', delta: `${Math.abs(next7Chg).toFixed(1)}%`,
      body: <>I'm forecasting blended ROAS around <b>{fmtX(next7)}</b> this week if the winners hold. I reforecast this every morning.</> },
    { shape: 'circles', colors: ['teal', 'cyan', 'blue'], title: 'What I did today', tag: "From today's activity",
      body: <>I moved budget into <b>{short(best.c.name)}</b>, cut spend on <b>{short(worst.c.name)}</b>, and paused two tired ads. All logged for you.</> },
  ];

  return (
    <div className="v4-dash v4-mark-dash">
      <div className="v4-mark-inner">
        <header className="v4-mark-hero">
          <div className="v4-mark-avatar"><img src={markPhoto} alt="Mark" /></div>
          <h1>Hey Alicia, I'm Mark.</h1>
          <p className="v4-mark-tag">Your AI media buyer. I watch every dollar across the account, find what's working, and move budget to it. Here's everything I'm seeing right now.</p>
          <span className="v4-mark-livechip"><i /> Live · running your account</span>
        </header>

        <section className="v4-mark-cards">
          {cards.map((c, i) => (
            <div className="v4-mark-card" key={i}>
              <div className="mc-ico"><LayeredIco shape={c.shape} colors={c.colors} /></div>
              <h4 className="mc-title">{c.title}</h4>
              <p className="mc-body">{c.body}</p>
              <span className={'mc-tag' + (c.dir ? ' ' + c.dir : '')}>
                {c.dir
                  ? <><b className="mc-dir">{c.dir === 'up' ? '▲' : '▼'} {c.delta}</b><span className="mc-sep">·</span>{c.tag}</>
                  : <><SparkTag /> {c.tag}</>}
              </span>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
